"use client";

import { X, ChevronDown, Loader2, Eye, EyeOff } from "lucide-react";
import type React from "react";
import { useState, useEffect, useRef } from "react";
import useApiHook from "../../utils/useApi";
import {
  InviteModalProps,
  InviteUserData,
  Role,
} from "../../interface/interfaces";

const InviteModal: React.FC<InviteModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
}) => {
  const [formData, setFormData] = useState<InviteUserData>({
    emailAddress: "",
    lastName: "",
    firstName: "",
    phoneNumber: "",
    roleReference: "",
    password: "",
  });
  const [isRoleDropdownOpen, setIsRoleDropdownOpen] = useState(false);
  const [selectedRoleName, setSelectedRoleName] = useState<string | null>(null);
  const [roles, setRoles] = useState<Role[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const modalRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // API hook for fetching roles
  const { data, isLoading, makeRequest } = useApiHook<{
    data: {
      totalCount: number;
      page: number;
      pageSize: number;
      totalPages: number;
      data: Role[];
    };
    statusCode: string;
    status: string;
  }>("/admin/roles", {
    method: "get",
  });

  // Initial data load for roles
  useEffect(() => {
    makeRequest({ page: 1, size: 10 });
  }, []);

  // Handle data from API
  useEffect(() => {
    if (data?.data?.data) {
      if (page === 1) {
        setRoles(data.data.data);
      } else {
        setRoles((prevRoles) => [...prevRoles, ...data.data.data]);
      }

      // Check if there are more items to load
      setHasMore(data.data.data.length === 10);
      setIsLoadingMore(false);
    }
  }, [data]);

  // Load more data when scrolling to bottom
  const handleScroll = () => {
    const container = scrollContainerRef.current;
    if (!container || isLoadingMore || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    if (scrollHeight - scrollTop <= clientHeight * 1.5) {
      setIsLoadingMore(true);
      const nextPage = page + 1;
      setPage(nextPage);
      makeRequest({ page: nextPage, size: 10 });
    }
  };

  // Close modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isRoleDropdownOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsRoleDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isRoleDropdownOpen]);


  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEsc);
    }

    return () => {
      document.removeEventListener("keydown", handleEsc);
    };
  }, [isOpen, onClose]);

  // Prevent body scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name === "password") {
      if (value.length > 0 && value.length < 10) {
        setPasswordError("Password must be at least 10 characters");
      } else {
        setPasswordError("");
      }
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle role selection
  const handleRoleSelect = (roleReference: string, roleName: string) => {
    setFormData((prev) => ({
      ...prev,
      roleReference,
    }));
    setSelectedRoleName(roleName);
    setIsRoleDropdownOpen(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    onSubmit(formData);

    setTimeout(() => {
      setIsSubmitting(false);
      setFormData({
        emailAddress: "",
        lastName: "",
        firstName: "",
        phoneNumber: "",
        roleReference: "",
        password: "",
      });
      setSelectedRoleName(null);
    }, 1000);
  };


  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };


  const isFormValid = () => {
    return (
      formData.emailAddress.trim() !== "" &&
      formData.lastName.trim() !== "" &&
      formData.firstName.trim() !== "" &&
      formData.phoneNumber.trim() !== "" &&
      formData.roleReference.trim() !== "" &&
      formData.password.trim() !== "" &&
      formData.password.length >= 8 &&
      !passwordError
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div
        ref={modalRef}
        className="bg-white rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto your-scroll-containeranimate-fadeIn"
      >
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-lg font-semibold">Invite team member</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4">
          {/* Email Address */}
          <div className="mb-4">
            <label
              htmlFor="emailAddress"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Email address
            </label>
            <input
              id="emailAddress"
              name="emailAddress"
              type="email"
              value={formData.emailAddress}
              onChange={handleInputChange}
              placeholder="colleague@example.com"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>

          {/* Password */}
          <div className="mb-4">
            <label
              htmlFor="password"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Password
            </label>
            <div className="relative">
              <input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={handleInputChange}
                placeholder="Enter password"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent pr-10"
              />
              <button
                type="button"
                onClick={togglePasswordVisibility}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>
            {passwordError && (
              <p className="text-red-500 text-xs mt-1">{passwordError}</p>
            )}
          </div>

          {/* First Name */}
          <div className="mb-4">
            <label
              htmlFor="firstName"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              First Name
            </label>
            <input
              id="firstName"
              name="firstName"
              type="text"
              value={formData.firstName}
              onChange={handleInputChange}
              placeholder="John"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>

          {/* Last Name */}
          <div className="mb-4">
            <label
              htmlFor="lastName"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Last Name
            </label>
            <input
              id="lastName"
              name="lastName"
              type="text"
              value={formData.lastName}
              onChange={handleInputChange}
              placeholder="Doe"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>

          {/* Phone Number */}
          <div className="mb-4">
            <label
              htmlFor="phoneNumber"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Phone Number
            </label>
            <input
              id="phoneNumber"
              name="phoneNumber"
              type="tel"
              value={formData.phoneNumber}
              onChange={handleInputChange}
              placeholder="08012345678"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>

          {/* Role Selection */}
          <div className="mb-6">
            <label
              htmlFor="role"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Role
            </label>
            <div className="relative" ref={dropdownRef}>
              <div
                className="w-full px-3 py-2 border border-gray-300 rounded-md flex justify-between items-center cursor-pointer focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                onClick={() => setIsRoleDropdownOpen(!isRoleDropdownOpen)}
              >
                <span
                  className={
                    selectedRoleName ? "text-gray-900" : "text-gray-400"
                  }
                >
                  {selectedRoleName || "Select a role"}
                </span>
                <ChevronDown className="h-4 w-4 text-gray-500" />
              </div>

              {/* Role Dropdown */}
              {isRoleDropdownOpen && (
                <div className="absolute left-0 right-0 mt-1 bg-white rounded-md shadow-lg z-10 border border-gray-200">
                  {isLoading ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      <span className="text-sm text-gray-500">
                        Loading roles...
                      </span>
                    </div>
                  ) : roles.length > 0 ? (
                    <div
                      className="max-h-48 overflow-y-auto"
                      ref={scrollContainerRef}
                      onScroll={handleScroll}
                    >
                      {roles.map((role) => (
                        <div
                          key={role.roleReference}
                          className={`px-3 py-2 cursor-pointer transition-colors ${
                            formData.roleReference === role.roleReference
                              ? "bg-green-50 text-green-700"
                              : "hover:bg-gray-50 text-gray-700"
                          }`}
                          onClick={() =>
                            handleRoleSelect(role.roleReference, role.roleName)
                          }
                        >
                          <span className="text-sm">{role.roleName}</span>
                        </div>
                      ))}

                      {/* Loading indicator at bottom while fetching more */}
                      {isLoadingMore && (
                        <div className="flex items-center justify-center py-2">
                          <Loader2 className="h-3 w-3 animate-spin mr-1" />
                          <span className="text-xs text-gray-500">
                            Loading more...
                          </span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="py-3 text-center text-sm text-gray-500">
                      No roles found
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!isFormValid() || isSubmitting}
              className={`px-4 py-2 rounded-md transition-colors flex items-center justify-center ${
                isFormValid() && !isSubmitting
                  ? "bg-green-500 hover:bg-green-600 text-white"
                  : "bg-green-300 text-white cursor-not-allowed"
              }`}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  <span>Inviting...</span>
                </>
              ) : (
                "Invite"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InviteModal;
